import {
  useMap,
  MapMouseEvent,
  MapGeoJSONFeature,
  Map,
  MapLayerTouchEvent,
} from 'shared/map-exports';
import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/capacitor';
import { getDeviceInfo } from 'state/localStorageTyped';
import { MapboxFeature } from 'features/mapping/mapping.types';

export type UseMapLayerEventsProps<T> = {
  layerId: string;
  /**
   * When `false`, no listeners are registered (and any registered ones are
   * removed). Registered layer listeners are not free even when idle: mapbox
   * runs feature-query work against the layer on every relevant mouse event,
   * so hidden layers should pass their `visible` prop here.
   */
  enabled: boolean;
  /**
   * Use only for handling map-layer–level click events that rely on
   * contextual data available at the layer/component scope rather than
   * at the individual feature level.
   *
   * Do not use this for router drawer entities, POIs, map drawer entity POIs,
   * or route-based redirections, those should be handled by the
   * `useMapLayerClickEvents` hook.
   *
   * Example: selecting a structured evacuation zone on the Wildfire,
   * Flooding, or Report forms.
   */
  onClick?: (features: MapboxFeature<T>[]) => void;
  onHover?: (features: MapboxFeature<T>[], map: Map) => void;
  onHoverOff?: (map: Map) => void;
  stopTouchMouseEventPropagation?: boolean;
};

export const useMapLayerEvents = <T>(
  props: UseMapLayerEventsProps<T>,
): void => {
  const {
    layerId,
    enabled,
    onClick,
    onHover,
    onHoverOff,
    stopTouchMouseEventPropagation,
  } = props;
  const { current: mapRef } = useMap();
  const deviceInfo = getDeviceInfo();

  const isMobilePlatform = Boolean(deviceInfo?.isMobilePlatform);

  // Refs so handler identity changes don't trigger re-subscription; only
  // presence (set vs. unset) affects which listeners are registered.
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  const onHoverRef = useRef(onHover);
  onHoverRef.current = onHover;

  const onHoverOffRef = useRef(onHoverOff);
  onHoverOffRef.current = onHoverOff;

  const hasClick = Boolean(onClick);
  const hasHover = Boolean(onHover);
  const hasHoverOff = Boolean(onHoverOff);

  useEffect(() => {
    const map = mapRef?.getMap();

    if (!map || !enabled) {
      return () => {};
    }

    const handleClick = (event: MapMouseEvent): void => {
      if (onClickRef.current && event.features?.length) {
        onClickRef.current(event.features as MapboxFeature<T>[]);
      }
    };

    const handleMouseEnter = (): void => {
      const canvas = map.getCanvas();
      if (canvas) {
        canvas.style.cursor = 'pointer';
      }
    };

    const handleMouseLeave = (): void => {
      const canvas = map.getCanvas();
      if (canvas) {
        canvas.style.cursor = 'auto';
      }

      onHoverOffRef.current?.(map);
    };

    const handleMouseMove = (event: MapMouseEvent): void => {
      let features: MapGeoJSONFeature[] = [];
      try {
        // Getting error "Unhandled feature index out of bounds" on Sentry
        // https://trello.com/c/2foNOR9Y/1475-investigate-new-ish-frontend-exception-error-feature-index-out-of-bounds-new-as-of-alert-cameras-release
        features = map.queryRenderedFeatures(event.point, {
          layers: [layerId],
        });

        if (onHoverRef.current && features.length) {
          onHoverRef.current(features as MapboxFeature<T>[], map);
        }
      } catch (err) {
        Sentry.withScope((scope): void => {
          scope.setContext('handleMouseMove', {
            eventPoint: event.point,
            layerId,
            featuresLength: features.length,
            mapCenter: map.getCenter(),
            mapZoom: map.getZoom(),
            mapBounds: map.getBounds()?.toString(),
          });
          Sentry.captureException(err);
        });
      }
    };

    const handleMouseDown = (event: MapMouseEvent): void => {
      event.originalEvent.stopPropagation();
    };

    const handleTouchStart = (event: MapLayerTouchEvent): void => {
      event.originalEvent.stopPropagation();
    };

    const isInteractive = hasClick || hasHover || hasHoverOff;

    if (hasClick) {
      map.on('click', layerId, handleClick);
    }
    if (isInteractive) {
      map.on('mouseenter', layerId, handleMouseEnter);
      map.on('mouseleave', layerId, handleMouseLeave);
    }
    if (hasHover && !isMobilePlatform) {
      map.on('mousemove', layerId, handleMouseMove);
    }
    if (stopTouchMouseEventPropagation) {
      map.on('mousedown', layerId, handleMouseDown);
      map.on('touchstart', layerId, handleTouchStart);
    }

    return () => {
      if (hasClick) {
        map.off('click', layerId, handleClick);
      }
      if (isInteractive) {
        map.off('mouseenter', layerId, handleMouseEnter);
        map.off('mouseleave', layerId, handleMouseLeave);
      }
      if (hasHover && !isMobilePlatform) {
        map.off('mousemove', layerId, handleMouseMove);
      }
      if (stopTouchMouseEventPropagation) {
        map.off('mousedown', layerId, handleMouseDown);
        map.off('touchstart', layerId, handleTouchStart);
      }
    };
  }, [
    mapRef,
    layerId,
    enabled,
    hasClick,
    hasHover,
    hasHoverOff,
    isMobilePlatform,
    stopTouchMouseEventPropagation,
  ]);
};
